import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../css/home.css";
import Navbar from "../components/navbar";
import { Helmet } from "react-helmet-async";
import Footer from "../components/footer";

export default function Home() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "https://resumify.co.uk"
      : "http://localhost:5000";

  // --- RESPONSIVE WINDOW TRACKING FOR CV SCALE ---
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Dynamically calculate the CV scale based on screen size
  let cvScale = 0.65;
  if (windowWidth < 1200) cvScale = 0.55;
  if (windowWidth < 992) cvScale = 0.45;
  if (windowWidth < 768) cvScale = 0.32;

  const scaledWidth = 800 * cvScale;
  const scaledHeight = 1131 * cvScale;

  // --- DYNAMIC: Scroll-Triggered Stats (FIXED REF LOGIC) ---
  const [stats, setStats] = useState({ ats: 0, interviews: 0, cvs: 0 });
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }, // Lowered threshold to ensure it fires immediately on load
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!statsVisible) return;
    const interval = setInterval(() => {
      setStats((prev) => ({
        ats: Math.min(prev.ats + 2, 98),
        interviews: Math.min(prev.interviews + 1, 3),
        cvs: Math.min(prev.cvs + 250, 15000),
      }));
    }, 40);
    return () => clearInterval(interval);
  }, [statsVisible]);

  // --- DYNAMIC: Parallax Mouse Tilt for Hero Mockup ---
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const handleMouseMove = (e) => {
    const { clientWidth, clientHeight } = e.currentTarget;
    const { left, top } = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - left - clientWidth / 2) / 30;
    const y = -(e.clientY - top - clientHeight / 2) / 30;
    setTilt({ x, y });
  };
  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  // --- AI Animation States ---
  const [aiPhase, setAiPhase] = useState(0);
  const [typedContent, setTypedContent] = useState("");
  const aiText =
    "Engineered a scalable React frontend, increasing performance by 40% and user retention by 25%.";

  useEffect(() => {
    let timeout;
    let typingInterval;

    if (aiPhase === 0) {
      setTypedContent("");
      timeout = setTimeout(() => setAiPhase(1), 2000);
    } else if (aiPhase === 1) {
      timeout = setTimeout(() => setAiPhase(2), 1000);
    } else if (aiPhase === 2) {
      timeout = setTimeout(() => setAiPhase(3), 400);
    } else if (aiPhase === 3) {
      let charIndex = 0;
      typingInterval = setInterval(() => {
        if (charIndex <= aiText.length) {
          setTypedContent(aiText.slice(0, charIndex));
          charIndex += 2;
        } else {
          clearInterval(typingInterval);
          timeout = setTimeout(() => setAiPhase(4), 500);
        }
      }, 20);
    } else if (aiPhase === 4) {
      timeout = setTimeout(() => setAiPhase(0), 4500);
    }

    return () => {
      clearTimeout(timeout);
      clearInterval(typingInterval);
    };
  }, [aiPhase, aiText]);

  // --- LUCRATIVE: Template Showcase State ---
  const [activeTemplate, setActiveTemplate] = useState("kinetic");
  const showcaseTemplates = [
    { id: "kinetic", name: "Kinetic", desc: "Hard Brutalism & Tech" },
    { id: "editorial", name: "Editorial", desc: "High-End Publication" },
    { id: "metric", name: "Metric", desc: "Solid Right Sidebar" },
    { id: "syntax", name: "Syntax", desc: "Code-Inspired Split" },
  ];

  const handleBuilderClick = async () => {
    if (!user) {
      navigate("/builder");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/cvs/load`, {
        credentials: "include",
      });

      if (res.ok) {
        const data = await res.json();
        if (data.hasCV) {
          localStorage.setItem("cvDraft", JSON.stringify(data.formData));
          navigate("/builder", {
            state: { isEdit: true, cvId: data.id, title: data.title },
          });
        } else {
          localStorage.removeItem("cvDraft");
          navigate("/builder", { state: { isNew: true } });
        }
      } else {
        navigate("/builder");
      }
    } catch (error) {
      console.error("Failed to load recent CV:", error);
      navigate("/builder");
    }
  };

  // --- HELPER RENDERS ---
  const renderHeadlines = () => (
    <>
      <h1>
        Stop Getting Ignored.
        <br />
        <span className="text-gradient">Start Getting Interviews.</span>
      </h1>
      <p className="hero-sub">
        Our advanced Builder combines <strong>12 professional templates</strong>
        , real-time design controls, and{" "}
        <strong>AI-powered writing assistance</strong> to create CVs that pass
        ATS filters and impress human recruiters.
      </p>
    </>
  );

  const renderTrust = () => (
    // Removed the ref from here so it doesn't get rendered twice
    <div className="hero-trust">
      <span>
        <span className="check">✔</span> {stats.cvs.toLocaleString()}+ CVs Built
      </span>
      <span>
        <span className="check">✔</span> {stats.ats}% ATS Pass Rate
      </span>
    </div>
  );

  const renderCTA = () => (
    <div className="hero-buttons">
      <button
        className="cta-button primary-dynamic"
        onClick={handleBuilderClick}
      >
        Build My CV Now 🚀
      </button>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Resumify | AI CV Builder</title>
        <meta
          name="description"
          content="Build a professional, ATS-friendly CV in minutes. Use our AI writing assistant, real-time design controls, and premium templates to land more interviews."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://resumify.co.uk/" />
        <meta property="og:title" content="Resumify | AI CV Builder" />
        <meta
          property="og:description"
          content="Build a professional, ATS-friendly CV in minutes. Use our AI writing assistant, real-time design controls, and premium templates to land more interviews."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://resumify.co.uk/" />
      </Helmet>

      <div className="home-page">
        <Navbar />

        {/* HERO SECTION - Ref attached here where it is always mounted and visible */}
        <header className="hero" ref={statsRef}>
          <div className="hero-container">
            {/* 1. DESKTOP VIEW */}
            <div className="hero-content desktop-only">
              {renderHeadlines()}
              {renderCTA()}
              {renderTrust()}
            </div>

            {/* DYNAMIC CV VISUAL */}
            <div
              className="hero-visual"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              style={{ perspective: "1000px" }}
            >
              <div
                className="dynamic-cv-mockup"
                style={{
                  transform: `rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
                  transition: "transform 0.1s ease-out",
                }}
              >
                {/* 2. MOBILE TOP TEXT */}
                <div className="hero-content mobile-only cv-embedded-hero">
                  {renderHeadlines()}
                  {renderTrust()}
                </div>

                <div className="cv-header">
                  <div className="cv-name-block shimmer"></div>
                  <div className="cv-role-text">Full-Stack Developer</div>
                </div>

                <div className="cv-body">
                  <div className="cv-section">
                    <div className="cv-section-title">Experience</div>

                    {/* Standard Static Block */}
                    <div className="cv-item" style={{ marginBottom: "20px" }}>
                      <div className="cv-item-title shimmer delay-1"></div>
                      <div className="cv-item-lines">
                        <div className="line full shimmer delay-2"></div>
                        <div className="line full shimmer delay-3"></div>
                      </div>
                    </div>

                    {/* AI Animated Block */}
                    <div className="cv-item">
                      <div className="cv-item-title shimmer delay-2"></div>
                      <div
                        className={`cv-item-content ${
                          aiPhase >= 1 && aiPhase < 4 ? "ai-active" : ""
                        }`}
                      >
                        {/* Floating Glassmorphic AI Button */}
                        <div
                          className={`cv-ai-button ${
                            aiPhase >= 1 ? "visible" : ""
                          } ${aiPhase === 2 ? "clicked" : ""}`}
                          style={{
                            backdropFilter: "blur(10px)",
                            background: "rgba(37, 99, 235, 0.15)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            color: "#fff",
                          }}
                        >
                          ✨ AI Rewrite
                        </div>

                        {/* Toggles between shimmers and typed text */}
                        {aiPhase <= 2 ? (
                          <div className="cv-item-lines">
                            <div className="line full shimmer delay-4"></div>
                            <div className="line almost-full shimmer delay-1"></div>
                          </div>
                        ) : (
                          <div className="cv-typed-text">
                            {typedContent}
                            {aiPhase !== 0 && (
                              <span className="cv-cursor"></span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="cv-section mobile-center-skills">
                    <div className="cv-section-title">Core Skills</div>
                    <div className="cv-skills">
                      <span className="cv-badge fade-in-1">React</span>
                      <span className="cv-badge fade-in-2">Node.js</span>
                      <span className="cv-badge fade-in-3">Express</span>
                      <span className="cv-badge fade-in-4">MySQL</span>
                    </div>
                  </div>
                </div>

                {/* 3. MOBILE BOTTOM CTA */}
                <div className="hero-content mobile-only cv-embedded-cta">
                  {renderCTA()}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* SOCIAL PROOF TICKER */}
        <div className="social-proof-banner">
          <p>Candidates using our templates have been hired at</p>
          <div className="logo-ticker">
            <span>Shopify</span>
            <span>HubSpot</span>
            <span>Zendesk</span>
            <span>Dropbox</span>
            <span>Canva</span>
            <span>Mailchimp</span>
          </div>
        </div>

        {/* --- LIVE TEMPLATE SHOWCASE --- */}
        <section
          className="template-showcase-section"
          style={{
            padding: "100px 20px",
            textAlign: "center",
            borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
          }}
        >
          <h2>12 Layouts. Zero Formatting Nightmares.</h2>
          <p
            style={{
              color: "#a1a1aa",
              marginBottom: "60px",
              maxWidth: "600px",
              margin: "0 auto 60px auto",
            }}
          >
            Click to preview how your content instantly adapts to different
            industry standards. Our engine handles margins, padding, and perfect
            A4 scaling automatically.
          </p>

          <div
            className="showcase-container"
            style={{
              display: "flex",
              gap: "64px",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            {/* 1. Interactive Selector */}
            <div
              className="showcase-controls"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                textAlign: "left",
                flex: "1 1 300px",
                maxWidth: "400px",
              }}
            >
              {showcaseTemplates.map((tpl) => {
                const isActive = activeTemplate === tpl.id;
                return (
                  <button
                    key={tpl.id}
                    onClick={() => setActiveTemplate(tpl.id)}
                    style={{
                      padding: "22px 28px",
                      backgroundColor: isActive ? "#6d28d9" : "#1e293b",
                      borderColor: isActive ? "#c084fc" : "#64748b",
                      borderWidth: "2px",
                      borderStyle: "solid",
                      borderRadius: "16px",
                      color: "#fff",
                      cursor: "pointer",
                      transition:
                        "background-color 0.3s ease, border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease",
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      boxShadow: isActive
                        ? "0 10px 30px -5px rgba(147, 51, 234, 0.5)"
                        : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      transform: isActive ? "scale(1.02)" : "scale(1)",
                    }}
                  >
                    <strong
                      style={{
                        fontSize: "1.2rem",
                        letterSpacing: "0.5px",
                        color: "#ffffff",
                      }}
                    >
                      {tpl.name}
                    </strong>
                    <span
                      style={{
                        color: isActive ? "#e9d5ff" : "#cbd5e1",
                        fontSize: "0.95rem",
                        fontWeight: "500",
                      }}
                    >
                      {tpl.desc}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* 2. Massive Scaled DOM Render for Desktop */}
            <div
              className="mini-cv-wrapper"
              style={{
                width: `${scaledWidth}px`,
                height: `${scaledHeight}px`,
                overflow: "hidden",
                borderRadius: "12px",
                boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
                background: "#ffffff",
                position: "relative",
                flexShrink: 0,
                transition: "width 0.3s ease, height 0.3s ease",
              }}
            >
              <div
                className={`a4-document ${activeTemplate}`}
                style={{
                  transform: `scale(${cvScale})`,
                  transformOrigin: "top left",
                  pointerEvents: "none",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  transition: "opacity 0.2s ease-in-out",
                }}
              >
                <div className="res-header">
                  <div className="res-header-text">
                    <h1>Alex Mercer</h1>
                    <div className="res-role">Full-Stack Engineer</div>
                  </div>
                  <div className="res-contact">
                    <span>hello@alexmercer.dev</span>
                    <span>Birmingham, UK</span>
                  </div>
                </div>

                <div className="res-body">
                  <div className="res-section">
                    <h2 className="res-section-title">Experience</h2>
                    <div className="res-job">
                      <div className="job-header">
                        <span className="job-title">Senior Developer</span>
                        <span className="job-company">TechFlow</span>
                        <span className="job-date">2024 - Present</span>
                      </div>
                      <ul className="res-list">
                        <li>
                          Engineered a scalable React frontend, increasing
                          performance by 40%.
                        </li>
                        <li>
                          Architected secure Node.js microservices reducing
                          downtime to zero.
                        </li>
                      </ul>
                      <div className="res-skills">
                        <span className="res-skill-badge">React</span>
                        <span className="res-skill-badge">Node.js</span>
                        <span className="res-skill-badge">MySQL</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES SECTION */}
        <section className="features-section">
          <div className="section-inner">
            <h2>Inside The CV Builder</h2>
            <p className="section-lead">
              We built a studio-grade editor that works seamlessly on desktop
              and mobile. Here is what you get access to inside.
            </p>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">🎨</div>
                <h3>12 Premium Templates</h3>
                <p>
                  Switch instantly between 12 distinct styles including{" "}
                  <strong>Signature</strong>,<strong> Elevate</strong>,{" "}
                  <strong>Editorial</strong>, and even bold choices like{" "}
                  <strong>Kinetic</strong> or <strong>Syntax</strong>.
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">✨</div>
                <h3>AI Writing Assistant</h3>
                <p>
                  Stuck on what to say? Click the <strong>AI Assist</strong>{" "}
                  button in any section to generate high-impact, metrics-driven
                  bullet points tailored to your specific role.
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🖌</div>
                <h3>Real-Time Design Controls</h3>
                <p>
                  Select custom accent colours and choose from professional
                  typography pairings to make your CV uniquely yours, with
                  changes reflecting instantly.
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">📱</div>
                <h3>Edit Anywhere</h3>
                <p>
                  Our builder features a dedicated{" "}
                  <strong>mobile-view toggle</strong>, allowing you to
                  comfortably edit sections, tweak designs, and preview your CV
                  right from your phone.
                </p>
              </div>
            </div>

            <div className="cta-wrapper features-cta">
              <button
                className="secondary-button"
                onClick={() => navigate("/how-it-works")}
              >
                How It Works
              </button>
            </div>
          </div>
        </section>

        {/* COMPARISON SECTION */}
        <section className="comparison">
          <div className="section-inner">
            <h2>Not Just Another CV Builder</h2>
            <div className="comparison-grid">
              <div className="compare-box basic">
                <h3>Typical Builders ❌</h3>
                <ul>
                  <li>Rigid, boring templates</li>
                  <li>Manual, tedious rewriting</li>
                  <li>Hidden paywalls at export</li>
                  <li>Clunky on mobile phones</li>
                </ul>
              </div>

              <div className="compare-box highlight">
                <div className="popular-tag">Your Unfair Advantage</div>
                <h3>Our Platform ✅</h3>
                <ul>
                  <li>
                    <strong>12+ Dynamic Styles</strong> tailored to your
                    industry
                  </li>
                  <li>
                    <strong>AI-written</strong> achievement bullets on demand
                  </li>
                  <li>
                    <strong>Flawless PDF Export</strong> that retains your exact
                    design
                  </li>
                  <li>
                    <strong>Mobile-Optimised</strong> workspace with smart
                    toggles
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="final-cta">
          <div className="final-cta-content">
            <h2>Your Next Interview Starts Here</h2>
            <p>
              Join thousands of job seekers landing their dream roles. Jump into
              the builder and see the difference a great CV makes.
            </p>
            <div className="cta-wrapper">
              <button
                className="cta-button cta-large primary-dynamic"
                onClick={handleBuilderClick}
              >
                Open The Builder
              </button>
            </div>
            <div className="final-notes-container">
              <span className="final-note">
                🔒 Secure checkout • Cancel anytime
              </span>
              <span className="desktop-recommendation">
                💻 For the best building experience, we recommend using a
                desktop or laptop.
              </span>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}
